import { PlannerConfig, ConfigOptions, FeatureConfig } from '../@types';
import eketCatalog from '@inter-ikea-storageone/range-eket/data/catalog.json';
import eketTemplate from '@inter-ikea-storageone/range-eket/data/template.json';
import mediaCatalog from '@inter-ikea-storageone/range-besta/data/media/catalog.json';
import mediaTemplate from '@inter-ikea-storageone/range-besta/data/media/template.json';
import proppingCatalog from '@inter-ikea-storageone/range-besta-eket-propping/data/catalog.json';
import proppingTemplate from '@inter-ikea-storageone/range-besta-eket-propping/data/template.json';
import { eketComponentResolver } from '@inter-ikea-storageone/range-eket';
import { plannerComponentResolver } from '@inter-ikea-storageone/range';
import frames from './productGroups/frames';
import platform from 'rangeConfig/shared/utils/platform';
import EketCameraBehavior from './camera/eketCameraBehavior';
import legs from 'rangeConfig/eket/productGroups/legs';
import { getOptimizedModels } from 'rangeConfig/shared/utils/optimizedModels';
import MeasureAction from 'components/SceneMenu/actions/MeasureAction';
import ProppingAction from 'components/SceneMenu/actions/ProppingAction';
import sprToVpcMap from '@inter-ikea-storageone/range-eket/data/spr2vpc.json';
import eketLandingPageConfig from './eketLandingPageConfig';
import { DistanceMeasurementFeature } from 'rangeConfig/shared/features/DistanceMeasurementFeature';
import { MultiWallSeeThroughFeature } from 'rangeConfig/shared/features/MultiWallSeeThroughFeature';
import { EkrisFeature } from 'rangeConfig/eket/features/Ekris/EkrisFeature';
import { TVFeature } from 'rangeConfig/shared/features/TVFeature/TVFeature';
import mediaFrames from './productGroups/media';

const defaultAssetsPath = '/assets/';
const assetsPath = import.meta.env.REACT_APP_ASSETS_PATH || defaultAssetsPath;
const screensaverPath = `${assetsPath}screensaver/screensaver_EKET.mp4`;

const features = (): { mobile: FeatureConfig[]; desktop: FeatureConfig[] } => {
  return {
    mobile: [
      { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
      { id: 'TVFeature', feature: TVFeature }
    ],
    desktop: [
      { id: 'MultiwallSeeThroughFeature', feature: MultiWallSeeThroughFeature },
      { id: 'Ekris', feature: EkrisFeature },
      { id: 'DistanceMeasurments', feature: DistanceMeasurementFeature },
      { id: 'TVFeature', feature: TVFeature }
    ]
  };
};

const productGroups: any = () => {
  const productGroups = {
    desktop: [frames, legs],
    mobile: [frames, mediaFrames, legs]
  };

  return productGroups[platform].map(group => group());
};

const sceneActions = (usePropping: boolean) => ({
  desktop: usePropping ? [MeasureAction, ProppingAction] : [MeasureAction],
  mobile: [MeasureAction]
});

const eketPlannerConfig = (options?: ConfigOptions): PlannerConfig => {
  const usePropping = !!options?.featureFlags?.eketPropping;
  return {
    productCatalogs: usePropping
      ? [
          {
            name: 'eket',
            catalog: getOptimizedModels(eketCatalog),
            template: eketTemplate,
            assetsPath
          },
          {
            name: 'media',
            catalog: getOptimizedModels(mediaCatalog),
            template: mediaTemplate,
            assetsPath
          },
          {
            name: 'propping',
            catalog: getOptimizedModels(proppingCatalog),
            template: proppingTemplate,
            assetsPath
          }
        ]
      : [
          {
            name: 'eket',
            catalog: getOptimizedModels(eketCatalog),
            template: eketTemplate,
            assetsPath
          },
          {
            name: 'media',
            catalog: getOptimizedModels(mediaCatalog),
            template: mediaTemplate,
            assetsPath
          }
        ],
    sprToVpcMap,
    componentResolvers: [plannerComponentResolver, eketComponentResolver],
    featureToggles: [],
    productGroups: productGroups(options),
    features: features()[platform],
    disabledFeatures: {
      assemblyInstructions: true
    },
    screensaverPath: screensaverPath,
    cameraBehavior: EketCameraBehavior,
    sceneActions: sceneActions(usePropping)[platform],
    landingPageConfig: eketLandingPageConfig
  };
};

export default eketPlannerConfig;
